import type { ParameterValue } from '@robovision/data'
import {
  packRecord,
  ParameterRecordBuilder,
} from '@robovision/data'
import type {
  HubDeploymentDependencyRefs,
  HubDeploymentRuntimeParameters,
  HubExampleStream,
  HubJobDependencyRefs,
  HubResource,
} from '@robovision/hub'
import { filterObject } from '@robovision/rvai-utils'
import { DeploymentDefinition } from '~domain/algorithm/jobDefinitions/DeploymentDefinition'
import { JobDefinition } from '~domain/algorithm/jobDefinitions/JobDefinition'
import type { ParameterValues } from '~domain/jobDataV2/ParameterValues'
import {
  BaseJobData,
  BaseJobDataDatapools,
  InferenceJobData,
} from '~domain/projects/JobData'
import { ModelResource } from '~domain/resourcesV2/ModelResource'
import { hubApi } from '~services/api'


interface BuildJobDependenciesParams {
  exampleStream?: HubExampleStream
  jobData: InferenceJobData | BaseJobData
  jobDefinition: DeploymentDefinition | JobDefinition
  model?: HubResource | ModelResource
  models?: Record<string, HubResource | ModelResource>
  projectId: string
}

type DependencyOutput = HubJobDependencyRefs | HubDeploymentDependencyRefs

export async function buildJobDependencies ({
  exampleStream,
  jobData,
  jobDefinition,
  model,
  models,
  projectId,
}: BuildJobDependenciesParams): Promise<DependencyOutput> {
  const dependencies: DependencyOutput = {}

  // map datapool dependencies
  const { datapools } = buildJobDatapoolsDependency({
    datapools: jobData.datapools,
    jobDefinition,
  })
  dependencies.datapools = datapools

  // map dataset dependencies
  for (const [dependencyId, definition] of Object.entries(jobDefinition.datasetDependencies)) {
    const datasetsMap = jobData.datasets[definition.definitionId]
    if (!datasetsMap) {
      throw new Error(`Missing datasets of type ${definition.definitionId}`)
    }

    const datasets = datasetsMap[dependencyId] ?? []
    if (!definition.optional && !datasets.length) {
      throw new Error(`Missing datasets for dependency ${dependencyId} of type ${definition.definitionId}`)
    }

    dependencies.datasets = {
      ...dependencies.datasets,
      [dependencyId]: [...datasets],
    }
  }

  // map parameter dependencies
  const { parameters } = await buildJobParametersDependency({
    jobDefinition,
    parameters: jobData.parameters,
    projectId,
  })
  dependencies.parameters = parameters

  const availableModels = jobDefinition.job.dependencies?.models ?? []

  // map model dependencies
  // Multi model is supported for inference center but not others
  if (jobData.modelId) {
    if (!model) throw new Error(`Could not find model for job id ${jobData.modelId}`)

    for (const modelDependency of jobDefinition.job.dependencies?.models ?? []) {
      dependencies.models = {
        ...dependencies.models,
        [modelDependency.id]: model.id,
      }
    }
  } else if (jobData.models) {
    for (const modelDependency of availableModels) {
      const model = models?.[modelDependency.id]
      if (!model) throw new Error(`Could not find model for job id ${jobData.models?.[modelDependency.id]}`)
      dependencies.models = {
        ...dependencies.models,
        [modelDependency.id]: model.id,
      }
    }
  }

  const {
    cameraConfigurationCalibrationResourceId,
    cameraConfigurationId,
  } = jobData as InferenceJobData

  if (cameraConfigurationId) {
    if (!exampleStream) {
      throw new Error(`Could not find example stream for cameraConfigurationId: ${cameraConfigurationId}`)
    }

    const exampleStreamsDependency = Object.values(jobDefinition.exampleStreamsDependencies)
    if (exampleStreamsDependency[0]) {
      dependencies.exampleStreams = {
        ...dependencies.exampleStreams,
        [exampleStreamsDependency[0].dependencyId]: exampleStream.id,
      }
    }
  }

  const calibrationDependency = Object.values(jobDefinition.blobDependencies)
    .find(d => d.blobDefinition.mimetype?.startsWith('rv-calibration/'))

  if (calibrationDependency && cameraConfigurationCalibrationResourceId) {
    dependencies.blobs = {
      ...dependencies.blobs,
      [calibrationDependency.dependencyId]: cameraConfigurationCalibrationResourceId,
    }
  }

  return dependencies
}

export function buildRuntimeParameters (jobData: InferenceJobData | BaseJobData): HubDeploymentRuntimeParameters {
  return {
    // TODO create helper?
    ...(jobData.resources.main.gpu != null && {
      resources: {
        limits: {
          gpu: jobData.resources.main.gpu,
        },
      },
    }),
    ...('maxInactivity' in jobData && jobData.maxInactivity > 0 && {
      maxInactivityPeriod: jobData.maxInactivity,
    }),
    ...('instances' in jobData && jobData.instances > 0 && {
      replicas: jobData.instances,
    }),
  }
}


export function buildJobDatapoolsDependency ({
  datapools,
  jobDefinition,
}: {
  datapools?: BaseJobDataDatapools,
  jobDefinition: DeploymentDefinition | JobDefinition,
}): DependencyOutput {
  const dependencies: DependencyOutput = {}

  for (const [dependencyId, definition] of Object.entries(jobDefinition.datapoolDependencies)) {
    const datapoolsMap = datapools?.[definition.definitionId]
    if (!datapoolsMap) {
      throw new Error(`Missing datapools of type ${definition.definitionId}`)
    }

    const datapoolDep = datapoolsMap[dependencyId]
    if (!datapoolDep) {
      throw new Error(`Missing datapool for dependency ${dependencyId} of type ${definition.definitionId}`)
    }

    dependencies.datapools = {
      ...dependencies.datapools,
      [dependencyId]: datapoolDep,
    }
  }

  return dependencies
}

export async function buildJobParametersDependency ({
  jobDefinition,
  parameters,
  projectId,
}: {
  jobDefinition: DeploymentDefinition | JobDefinition,
  parameters: ParameterValues,
  projectId: string
}): Promise<DependencyOutput> {
  const dependencies: DependencyOutput = {}

  for (const [dependencyId, definition] of Object.entries(jobDefinition.parametersDefinitions)) {
    if (!definition) continue

    const filtered = filterObject(
      parameters[dependencyId] ?? {},
      value => value != null,
    )
    const record = ParameterRecordBuilder
      .withLogicalType(dependencyId)
      .addParameters(filtered as Record<string, ParameterValue>)
      .build()

    const data = await packRecord(record)

    const { data: resourceData } = await hubApi.resources.createParametersResource({
      definitionId: definition.definitionId,
      metadata: JSON.stringify({}),
      projectId,
      value: new File([data], 'params.fbs'),
    })

    dependencies.parameters = {
      ...dependencies.parameters,
      [dependencyId]: resourceData.id,
    }
  }

  return dependencies
}
